import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import { cn } from "@penmate/react-shared/lib/utils";
import { ensureMessagingOverlayRoot } from "@/components/ui/documentOverlay";

const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogClose = DialogPrimitive.Close;

const DocumentDialogPortal = (
  props: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Portal>,
) => {
  const container = ensureMessagingOverlayRoot();
  return (
    <DialogPrimitive.Portal {...props} container={container}>
      {props.children}
    </DialogPrimitive.Portal>
  );
};

const DocumentDialogOverlay = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "pointer-events-auto fixed inset-0 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    aria-hidden="true"
    style={{ zIndex: 10050, backgroundColor: "rgba(0,0,0,0.6)", ...(props as any).style }}
    {...props}
  />
));
DocumentDialogOverlay.displayName = "DocumentDialogOverlay";

const DocumentDialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DocumentDialogPortal>
    <DocumentDialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "pointer-events-auto fixed left-1/2 top-1/2 grid w-full max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4 rounded-2xl border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",
        className,
      )}
      style={{ zIndex: 10051, ...(props as any).style }}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DocumentDialogPortal>
));
DocumentDialogContent.displayName = DialogPrimitive.Content.displayName;

const DocumentDialogTitle = DialogPrimitive.Title;
const DocumentDialogDescription = DialogPrimitive.Description;

export {
  Dialog as DocumentDialog,
  DialogTrigger as DocumentDialogTrigger,
  DialogClose as DocumentDialogClose,
  DocumentDialogContent,
  DocumentDialogPortal,
  DocumentDialogOverlay,
  DocumentDialogTitle,
  DocumentDialogDescription,
};
